import type * as elation from '@seen/core/models/elation'
import { VisitNote } from '@seen/core/models/visitNote'
import { parseISO } from 'date-fns'
import type { JSX } from 'react'
import { useMemo } from 'react'

type BillingSectionData = elation.visitNotes.BillingSectionData

interface VisitNoteBillingWidgetProps {
  visitNote: VisitNote
}

function formatBillingDate(isoDate: string): string {
  try {
    const d = parseISO(isoDate)
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0')
    const dd = String(d.getUTCDate()).padStart(2, '0')
    const yyyy = d.getUTCFullYear()
    return `${mm}/${dd}/${yyyy}`
  } catch {
    return isoDate
  }
}

export function VisitNoteBillingWidget({
  visitNote,
}: VisitNoteBillingWidgetProps): JSX.Element | null {
  const billingData = useMemo<BillingSectionData | null>(() => {
    const billingSection = Object.values(visitNote.sections)
      .flat()
      .find((s) => VisitNote.isBillingSection(s))
    if (!billingSection?.text?.div) {
      return null
    }
    try {
      return JSON.parse(billingSection.text.div) as BillingSectionData
    } catch {
      return null
    }
  }, [visitNote])

  if (!billingData) {
    return null
  }

  return (
    <div className="mt-6 mb-4 rounded-lg border border-blue-100 bg-blue-50 p-4">
      <h3 className="mb-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
        For physician's record only:
      </h3>
      <div className="space-y-1 text-sm text-muted-foreground">
        {billingData.bills.map(
          (bill: BillingSectionData['bills'][number], idx: number) => (
            <div key={`${bill.id}-${idx}`} className="flex flex-wrap gap-2">
              <span className="font-medium text-foreground">
                Bill {idx + 1} :
              </span>
              <span className="font-mono text-xs leading-5">
                {(bill.dxs ?? []).join(', ')}
              </span>
              {bill.cpt && (
                <span className="font-mono text-xs leading-5 text-muted-foreground">
                  CPT: {bill.cpt}
                </span>
              )}
            </div>
          )
        )}
        <div className="mt-1">
          Billing Status:{' '}
          {billingData.billingStatus === 'Billed'
            ? `Billed on ${formatBillingDate(billingData.billingDate)} (#${billingData.refNumber})`
            : billingData.billingStatus}
        </div>
      </div>
    </div>
  )
}
